import React, { useEffect, useState } from 'react';
import BrandMark from './BrandMark';
import { captureLeadEmail } from '../utils/captureLeadEmail';

const UTM_STORAGE_KEY = 'rentual_lead_utm';
const UTM_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const readStoredUtm = () => {
  try {
    const raw = localStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.expiresAt || Date.now() > parsed.expiresAt) {
      localStorage.removeItem(UTM_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const storeUtm = (payload) => {
  try {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore storage errors
  }
};

const buildUtmPayload = () => {
  const stored = readStoredUtm();
  return stored?.data || {};
};

const LeadCaptureForm = () => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmData = {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_content: params.get('utm_content') || '',
      utm_term: params.get('utm_term') || '',
      landing_page_url: window.location.href,
      referrer_url: document.referrer || ''
    };
    const hasUtm = Object.values(utmData).some((val) => val && String(val).trim().length > 0);
    if (hasUtm) {
      storeUtm({
        data: utmData,
        expiresAt: Date.now() + UTM_TTL_MS
      });
    }
  }, []);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    propertyArea: '',
    units: '',
    role: '',
    workflow: '',
    goal: '',
    timeline: '',
    budget: '',
    source: ''
  });
  const [status, setStatus] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEmailBlur = () => {
    captureLeadEmail(formData.email, window.location.pathname, { context: 'lead_capture', full_name: formData.fullName, phone: formData.phone });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submission = {
      ...formData,
      ...buildUtmPayload()
    };
    console.log('Lead capture submit', submission);
    setStatus('Thanks! We’ll reach out shortly.');
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      propertyArea: '',
      units: '',
      role: '',
      workflow: '',
      goal: '',
      timeline: '',
      budget: '',
      source: ''
    });
  };

  return (
    <main className="App-main contact-page">
      <h2>Request a <BrandMark /> demo</h2>
      <p className="contact-intro">Tell us about your portfolio and we’ll show you leasing + rent payments in one flow.</p>
      <form className="auth-form contact-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="fullName">Full name</label>
          <input id="fullName" name="fullName" type="text" value={formData.fullName} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} onBlur={handleEmailBlur} required />
        </div>
        <div className="form-group">
          <label htmlFor="phone">Phone</label>
          <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label htmlFor="propertyArea">Property area</label>
          <input id="propertyArea" name="propertyArea" type="text" value={formData.propertyArea} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label htmlFor="units">Number of units</label>
          <input id="units" name="units" type="number" min="1" value={formData.units} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label htmlFor="role">Role</label>
          <select id="role" name="role" value={formData.role} onChange={handleChange} required>
            <option value="">Select role</option>
            <option value="owner">Owner</option>
            <option value="property-manager">Property Manager</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="workflow">Current workflow</label>
          <select id="workflow" name="workflow" value={formData.workflow} onChange={handleChange}>
            <option value="">Select workflow</option>
            <option value="spreadsheet">Spreadsheet</option>
            <option value="software">Other software</option>
            <option value="none">None</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="goal">Primary goal</label>
          <select id="goal" name="goal" value={formData.goal} onChange={handleChange}>
            <option value="">Select goal</option>
            <option value="fill-vacancy">Fill vacancy</option>
            <option value="screen-tenants">Screen tenants</option>
            <option value="manage-portfolio">Manage portfolio</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="timeline">Timeline to start</label>
          <select id="timeline" name="timeline" value={formData.timeline} onChange={handleChange}>
            <option value="">Select timeline</option>
            <option value="now">Now</option>
            <option value="30-days">30 days</option>
            <option value="90-days">90 days</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="budget">Budget range</label>
          <input id="budget" name="budget" type="text" value={formData.budget} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="source">How did you hear about us?</label>
          <select id="source" name="source" value={formData.source} onChange={handleChange}>
            <option value="">Select source</option>
            <option value="google-ads">Google Ads</option>
            <option value="facebook-instagram">Facebook/Instagram</option>
            <option value="linkedin">LinkedIn</option>
            <option value="referral">Referral</option>
            <option value="partner">Partner</option>
            <option value="organic">Organic Search</option>
            <option value="direct">Direct</option>
            <option value="event-webinar">Event/Webinar</option>
            <option value="other">Other</option>
          </select>
        </div>
        {status && <div className="form-status success">{status}</div>}
        <div className="form-actions">
          <button type="submit" className="primary-button">Submit</button>
        </div>
      </form>
    </main>
  );
};

export default LeadCaptureForm;
